import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

import type { ActionDispatcher } from './action-dispatcher';
import type { ActionRequest } from './action-request';
import type { ActionEvent } from './action-response';

/**
 * Handles an `ActionRequest` or `ActionResponse`.
 *
 * Most handlers handle the actions that they know about
 * and pass other requests to the next handler by calling
 * `next.handle(req)`.
 *
 * When handling an `ActionRequest`, the handler might
 * emit a stream of different events as well, not only
 * the final `ActionResponse`. A common scenario is
 * to emit an event when the action was started, which
 * is usually the `ActionStartEvent`.
 *
 * Furthermore, a handler might also subscribe to the
 * stream of events returned by a subsequent handler.
 * Using this approach might let you alter or act upon
 * emitted events.
 *
 * @example
 * // Using an enum here is extremely useful when
 * // utilizing the switch / case later
 * enum MyKnownActions {
 *  Test = 'test'
 * }
 *
 * class MyActionHandler implements ActionHandler {
 *  knownActions: MyKnownActions[] = [MyKnownActions.Test];
 *
 *  handleAction(req, next) {
 *    switch (req.action) {
 *      // Handle known actions
 *      case MyKnownActions.Test: return of(new ActionResponse(...));
 *      // Pass the request on to the next dispatcher
 *      default: return next.dispatch(req);
 *    }
 *  }
 *
 *  isExecuteable(action, next) {
 *    switch (action) {
 *      case MyKnownActions.Test: return of(true);
 *      default: return next.isExecuteable(action);
 *    }
 *  }
 * }
 */
export interface ActionHandler {
  /**
   * Array of actions known to this handler
   */
  knownActions: string[];

  /**
   * Identifies and handles a given action request.
   *
   * @param req Action request to handle
   * @param next Next handler in the chain
   *
   * @returns
   * An observable of the event streams.
   */
  handleAction(
    req: ActionRequest<unknown>,
    next: ActionDispatcher,
  ): Observable<ActionEvent<unknown>>;

  /**
   * @param action Action identifier
   * @param next Next handler in the chain
   *
   * @returns
   * An observable that emits whenever the executeable status of
   * an action changes. It emits `true`, when the action can be
   * executed.
   */
  isExecuteable(action: string, next: ActionDispatcher): Observable<boolean>;
}

/**
 * `ActionDispatcher` that applies an `ActionHandler` to an `ActionRequest`
 */
export class ActionHandlerDispatcher implements ActionDispatcher {
  constructor(
    /**
     * The next dispatcher in the chain
     */
    private next: ActionDispatcher,
    /**
     * Handler wrapped by this dispatcher
     */
    private handler: ActionHandler,
  ) {}

  public dispatch(
    req: ActionRequest<unknown>,
  ): Observable<ActionEvent<unknown>> {
    return this.handler.handleAction(req, this.next);
  }

  public isExecuteable(action: string): Observable<boolean> {
    return this.handler.isExecuteable(action, this.next);
  }
}

/**
 * [DI token](https://angular.io/guide/glossary#di-token)
 * for `ActionHandler`s.
 * Use it as a multiprovider for binding.
 *
 * @example
 * NgModule({
 *  providers: [
 *    { provide: ACTION_HANDLERS, useClass: ActionHandler1, multi: true },
 *    { provide: ACTION_HANDLERS, useClass: ActionHandler2, multi: true },
 *  ]
 * })
 * export class MyActionsModule
 */
export const ACTION_HANDLERS = new InjectionToken<ActionHandler[]>(
  'actions.action-handlers',
);
